import {schema} from '@/common/spec/spec-cache';
import _ from '@/common/helpers';
import api from '@/common/api';
import FilterSet from './filter-set';
import {CURRENCIES_LIST} from '../../data/currencies';
import {COUNTRIES_LIST} from '../../data/countries';
import {PAYMENT_CARD_BRANDS_LIST} from '../../data/payment-card-brands';
import FILTER_SOURCE_TYPES from './filter-source-type';

/** @module FilterOptions */

/**
 * @typedef {Object} Option
 * @property {string} value - Value of the option
 * @property {string} label - Label of the option
 */

/** Class representing options available in a FilterDefinition. */
export default class FilterOptions {
    static sourceToResourceMap = {
        [FILTER_SOURCE_TYPES.enum]: FilterOptions.enumResource,
        [FILTER_SOURCE_TYPES.definitions]: FilterOptions.definitionsResource,
        [FILTER_SOURCE_TYPES.currencies]: FilterOptions.currenciesResource,
        [FILTER_SOURCE_TYPES.countries]: FilterOptions.countriesResource,
        [FILTER_SOURCE_TYPES.paymentCardBrands]: FilterOptions.paymentCardBrandsResource,
        [FILTER_SOURCE_TYPES.rebillyAPI]: FilterOptions.rebillyAPIResource,
    };

    static otherEndpoints = {
        'coupons-redemptions': ['coupons', 'getAllRedemptions'],
        coupons: 'coupons',
    };

    options = null;

    /**
     * Retrieve filter options values from the source
     * @param {OptionsParams} options
     */
    constructor(options) {
        this.options = options;
    }

    /**
     * Get all available options in the resource
     * @param {string} query
     * @return [Option]
     */
    async getAll(query) {
        const resource = FilterOptions.sourceToResourceMap[this.options.source];
        return resource(this.options, query);
    }

    /**
     * Retrieve options from API definition file. Since this is a static
     * file all options that match options.path are retrieved. The
     * @param {OptionsParams} options
     * @return [Option]
     */
    static async definitionsResource(options) {
        const steps = options.path.split(FilterSet.delimiter);
        const result = steps.reduce((reduced, value) => {
            if (reduced !== null) {
                if (reduced.properties && value in reduced.properties) {
                    reduced = reduced.properties[value];
                    return reduced;
                } if (value in reduced) {
                    reduced = reduced[value];
                    return reduced;
                }
            }
            return null;
        }, schema);
        if (result === null) {
            throw new Error(`Unable to resolve path ${options.path} in spec`);
        }
        return result.map(item => ({value: item, label: _.capitalize(item)}));
    }

    /**
     * Retrieve options from filter definition.
     *
     * import FilterSet from '@/components/query/filter-set';
     *
     * new FilterSet({
     *  id: 'filter-set-id',
     *  store: vuex store,
     *  filterSpec: {
     *   name: {
     *    type: FILTER_TYPES.select,
     *    options: {
     *        source: FILTER_SOURCE_TYPES.enum,
     *        enum: [String, String, String]
     *    }
     *   }
     *  }
     * })
     *
     * @return [Option]
     */
    static async enumResource(options) {
        if (!options.enum) {
            throw new Error(`Add enum options in spec`);
        }
        return options.enum.map(value => ({value, label: _.capitalize(value)}));
    }

    /**
     * Retrieve options from currencies file. Since this is a static
     * file all options are retrieved
     * @return [Option]
     */
    static async currenciesResource() {
        return CURRENCIES_LIST;
    }

    /**
     * Retrieve options from countries file. Since this is a static
     * file all options are retrieved
     * @return [Option]
     */
    static async countriesResource() {
        return COUNTRIES_LIST;
    }

    /**
     * Retrieve options from payment card brands file. Since this is a static
     * file all options are retrieved.
     * @returns [Option]
     */
    static async paymentCardBrandsResource() {
        return PAYMENT_CARD_BRANDS_LIST;
    }

    /**
     * Retrieve options from a remote resource at Rebilly API
     * @param {OptionsParams} options
     * @param {string} query
     * @return Array<Option>
     */
    static async rebillyAPIResource(options, query) {
        /*
          * Some of the resources contain endpoints that aren't standard using 'getAll' or mapped
          * to same-named resource. We check for these first.
         */
        const specialEndpoint = {
            isIn: path => FilterOptions.otherEndpoints[path] !== undefined,
            getResource(path) {
                const entry = FilterOptions.otherEndpoints[path];
                if (Array.isArray(entry) && entry.length >= 1) {
                    return entry[0];
                }

                return entry;
            },
            getEndpoint(path) {
                const entry = FilterOptions.otherEndpoints[path];
                if (Array.isArray(entry) && entry.length === 2) {
                    return entry[1];
                }

                return 'getAll';
            },
        };

        const {path: endpoint, labelBuilder, value} = options;
        let collection;
        if (specialEndpoint.isIn(options.path)) {
            const resource = specialEndpoint.getResource(options.path);
            const specificEndpoint = specialEndpoint.getEndpoint(options.path);
            collection = await api.compositeClient[resource][specificEndpoint]({
                q: query,
                limit: 20,
            });
        } else {
            collection = await api.compositeClient[endpoint].getAll({q: query, limit: 20});
        }

        return collection.items.map(({fields}) => (
            {value: fields[value], label: labelBuilder(fields)}
        ));
    }
}
